import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Gift, ArrowLeft } from "lucide-react";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import { GIFT } from "../mock/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OwnersGiftApply = () => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".om-fade-up").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    working_toward: "",
    done_so_far: "",
    use_of_gift: "",
    why_matters: "",
    share_ok: "no",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | ok | err
  const [errMsg, setErrMsg] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${API}/gift-applications`, form);
      setStatus("ok");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("err");
      setErrMsg(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("apply-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Thank you state
  if (status === "ok") {
    return (
      <div className="min-h-screen bg-[#FFFDF6] text-[#2b1a20] font-sans-body">
        <Nav />
        <section className="min-h-screen flex items-center justify-center px-6 py-32">
          <div className="max-w-2xl mx-auto text-center om-fade-up is-visible">
            <div className="uppercase tracking-[0.28em] text-[0.72rem] text-[#605F26] mb-6">
              Application received
            </div>
            <h1 className="font-serif-display text-[#41232E] text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em] mb-10">
              Thank <em className="italic text-[#605F26]">you.</em>
            </h1>
            <div className="space-y-5 text-[#41232E]/85 text-lg leading-[1.75]">
              <p>
                I know it takes time to sit down and share something you&rsquo;re working toward.
                Thank you for telling me a little about yours.
              </p>
              <p>I read every submission.</p>
              <p>
                If you&rsquo;re selected for this quarter&rsquo;s $1,000 Owner&rsquo;s Gift,
                I&rsquo;ll reach out to you directly by email.
              </p>
              <p className="font-serif-display italic text-2xl text-[#605F26] pt-4">
                Either way, keep going.
              </p>
              <p className="text-base text-[#41232E]/75">
                Taking ownership starts long before anyone else notices what you&rsquo;re building.
              </p>
            </div>
            <div className="mt-14">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#41232E] text-[#FFFDF6] hover:bg-[#2c161e] transition-colors"
              >
                <ArrowLeft size={16} /> Back home
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF6] text-[#2b1a20] font-sans-body">
      <Nav />

      {/* Top split section — mirrors current Owner's Gift design */}
      <section className="pt-24 grid md:grid-cols-2 min-h-[560px] md:min-h-[720px] bg-[#FFFDF6]">
        {/* Left — light copy */}
        <div className="flex items-center px-6 md:px-16 lg:px-20 py-16 md:py-24">
          <div className="max-w-lg om-fade-up">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-[#41232E]/70 hover:text-[#41232E] mb-8 transition-colors"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>

            <div className="inline-flex items-center gap-2 bg-[#41232E]/5 border border-[#41232E]/12 rounded-full px-3 py-1.5 text-[0.7rem] tracking-[0.24em] uppercase text-[#41232E]/75 mb-8">
              <Gift size={12} /> {GIFT.cadence}
            </div>

            <h1 className="font-serif-display text-[#41232E] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
              The Owner&rsquo;s Gift
            </h1>
            <p className="mt-4 font-serif-display italic text-[#605F26] text-2xl md:text-3xl">
              $1,000 to invest in yourself.
            </p>

            <div className="mt-8 space-y-5 text-[#41232E]/80 text-base md:text-lg leading-relaxed">
              <p>
                Every quarter, I give $1,000 to someone who is taking ownership of something they
                want to change, build, learn or pursue in their life.
              </p>
              <p>There are no categories for what that has to look like.</p>
              <p>
                Maybe you&rsquo;re starting a business. Taking a class. Learning a new skill.
                Working on your health. Pursuing something creative. Making a career change. Getting
                a certification. Or taking a step toward something entirely your own.
              </p>
              <p>
                What matters to me is that you&rsquo;ve decided something matters to you and
                you&rsquo;re doing something about it.
              </p>
              <p>
                The $1,000 Owner&rsquo;s Gift is simply a little extra help taking the next step.
              </p>
            </div>

            <button
              type="button"
              onClick={scrollToForm}
              className="mt-10 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#41232E] text-[#FFFDF6] hover:bg-[#2c161e] transition-colors"
            >
              Apply for the next Owner&rsquo;s Gift <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Right — living room bg + amount card */}
        <div className="relative flex items-center justify-center px-6 md:px-12 py-16 md:py-24 overflow-hidden">
          <img
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_mindful-owner/artifacts/wvlynsvj_2fab0a2c17811d64c19ced10e52e562e.jpg"
            alt="A warm considered living room"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#41232E]/15" />

          <div className="relative om-fade-up">
            <div className="bg-[#FFFDF6] rounded-[1.75rem] shadow-[0_20px_60px_rgba(65,35,46,0.28)] p-10 md:p-14 lg:p-16 max-w-md text-center">
              <div className="uppercase tracking-[0.28em] text-[0.7rem] text-[#605F26] mb-6">
                Every quarter
              </div>
              <div className="font-serif-display text-[#41232E] text-[5.5rem] md:text-[7rem] leading-none">
                {GIFT.amount}
              </div>
              <div className="mt-6 font-serif-display italic text-[#605F26] text-2xl md:text-3xl">
                for someone doing the work.
              </div>
              <div className="mt-10 flex items-center justify-center gap-4 text-xs tracking-[0.22em] uppercase text-[#41232E]/60">
                <span>Apply</span>
                <span className="w-8 h-px bg-[#41232E]/25" />
                <span>Share</span>
                <span className="w-8 h-px bg-[#41232E]/25" />
                <span>Celebrate</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application form section — sky-blue background, centered */}
      <section id="apply-form" className="bg-[#C1D3D9] py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12 om-fade-up">
            <div className="uppercase tracking-[0.28em] text-[0.72rem] text-[#41232E]/70 mb-5">
              The application
            </div>
            <h2 className="font-serif-display text-[#41232E] text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em]">
              Tell me what you&rsquo;re{" "}
              <em className="italic">working toward.</em>
            </h2>
          </div>

          <form onSubmit={onSubmit} className="space-y-8 om-fade-up">
            {/* Name */}
            <Field
              label="Your name"
              hint="First + last name"
              required
            >
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors"
              />
            </Field>

            {/* Email */}
            <Field
              label="Email address"
              hint="So I can contact you if you're selected."
              required
            >
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors"
              />
            </Field>

            {/* Working toward */}
            <Field
              label="What are you working toward?"
              hint="Tell me a little about what you want to change, build, learn, experience or accomplish."
              required
            >
              <textarea
                required
                rows={5}
                maxLength={2500}
                value={form.working_toward}
                onChange={(e) => update("working_toward", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors resize-y"
              />
            </Field>

            {/* Done so far */}
            <Field
              label="What have you done so far?"
              hint="Big or small, what actions have you already taken toward it?"
            >
              <textarea
                rows={5}
                maxLength={2500}
                value={form.done_so_far}
                onChange={(e) => update("done_so_far", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors resize-y"
              />
            </Field>

            {/* How would you use the $1,000 */}
            <Field
              label="How would you use the $1,000?"
              hint="Be as specific as you can — a class, equipment, training, professional help, materials, childcare, travel, starting something, or whatever would meaningfully help you take the next step."
            >
              <textarea
                rows={5}
                maxLength={2500}
                value={form.use_of_gift}
                onChange={(e) => update("use_of_gift", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors resize-y"
              />
            </Field>

            {/* Why matters */}
            <Field
              label="Why does this matter to you?"
              hint=""
            >
              <textarea
                rows={5}
                maxLength={2500}
                value={form.why_matters}
                onChange={(e) => update("why_matters", e.target.value)}
                className="w-full bg-[#FFFDF6] rounded-xl px-5 py-3.5 border border-[#41232E]/15 focus:border-[#41232E] outline-none text-[#41232E] transition-colors resize-y"
              />
            </Field>

            {/* Sharing section — set apart visually */}
            <div className="pt-8 mt-4 border-t border-[#41232E]/15">
              <div className="uppercase tracking-[0.24em] text-[0.7rem] text-[#41232E]/70 mb-4">
                One last thing
              </div>
              <p className="text-[#41232E]/85 text-base leading-relaxed mb-6">
                If you are selected, I&rsquo;d love the opportunity to share your story with The
                Owner&rsquo;s Mind community so other people can see what you&rsquo;re working
                toward and perhaps follow your progress.
              </p>
              <div className="mb-4 font-serif-display text-[#41232E] text-xl md:text-2xl leading-snug">
                If selected, would you be open to having your story and photo shared in The
                Owner&rsquo;s Mind newsletter and social channels?
              </div>
              <div className="space-y-3 mb-5">
                <RadioOption
                  name="share_ok"
                  value="yes"
                  checked={form.share_ok === "yes"}
                  onChange={(v) => update("share_ok", v)}
                  label="Yes, I'd be open to that"
                />
                <RadioOption
                  name="share_ok"
                  value="no"
                  checked={form.share_ok === "no"}
                  onChange={(v) => update("share_ok", v)}
                  label="No, I'd prefer to keep it private"
                />
              </div>
              <p className="text-sm text-[#41232E]/70 leading-relaxed">
                Your answer has absolutely no impact on whether you are selected. If you are chosen,
                I will contact you before anything is shared and ask for your permission. Nothing
                will be published without your approval.
              </p>
            </div>

            {status === "err" && (
              <div className="text-sm text-[#41232E] bg-[#FFFDF6] border border-[#41232E]/25 rounded-xl px-4 py-3">
                {errMsg}
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#41232E] text-[#FFFDF6] font-medium hover:bg-[#2c161e] transition-colors disabled:opacity-70"
              >
                {status === "loading" ? "Sending your application\u2026" : "Send my application"}
                {status !== "loading" && <ArrowRight size={16} />}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, hint, required, children }) => (
  <div>
    <label className="block font-serif-display text-[#41232E] text-xl md:text-[1.4rem] leading-snug mb-1">
      {label}
      {required && <span className="text-[#605F26]"> *</span>}
    </label>
    {hint && (
      <p className="text-sm text-[#41232E]/70 leading-relaxed mb-3 max-w-lg">{hint}</p>
    )}
    {children}
  </div>
);

const RadioOption = ({ name, value, checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <span
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
        checked ? "border-[#41232E] bg-[#41232E]" : "border-[#41232E]/40 bg-[#FFFDF6]"
      }`}
    >
      {checked && <span className="w-2 h-2 rounded-full bg-[#FFFDF6]"></span>}
    </span>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="sr-only"
    />
    <span className="text-[#41232E]/90 group-hover:text-[#41232E] transition-colors">
      {label}
    </span>
  </label>
);

export default OwnersGiftApply;
